import { IDENTITY_ENDPOINTS, identityClient } from '@libs/api-proxy-client';
import { getENV } from '@libs/common-next';
import type {
    ConsumeExchangeTokenResponse,
    CreateExchangeTokenResponse,
    IdentityChangePasswordPayload,
    IdentityChangePasswordResponse,
    IdentityErrorResponse,
    IdentityLogoutResponse,
    IdentityResetPasswordPayload,
    IdentityResetPasswordResponse,
    IdentityRetrieveRegistrationTokenParams,
    IdentityRetrieveRegistrationTokenResponse,
} from '@libs/common-types';
import { getCountry, getLocale, isLocaleCA, ResponseWithParsedData } from '@libs/core';

type ApiClientResponse<T> = T | Error | ResponseWithParsedData<T>;

export async function createExchangeToken() {
    return await identityClient.get<CreateExchangeTokenResponse>(IDENTITY_ENDPOINTS.createExchangeToken, {
        useOldFetch: true,
    });
}

export async function consumeExchangeToken(exchangeToken: string) {
    return await identityClient.get<ConsumeExchangeTokenResponse>(IDENTITY_ENDPOINTS.consumeExchangeToken, {
        headers: {
            'x-petm-exchange-token': exchangeToken,
        },
        additionalSuccessCodes: [401],
        useOldFetch: true,
    });
}

export async function retrieveRegistrationToken(
    params: IdentityRetrieveRegistrationTokenParams
): Promise<ApiClientResponse<IdentityRetrieveRegistrationTokenResponse>> {
    const { code, grantType, redirectUri, source } = params;

    const query = `code=${encodeURIComponent(code)}&grantType=${encodeURIComponent(
        grantType
    )}&redirectUri=${encodeURIComponent(redirectUri)}&source=${encodeURIComponent(source)}`;

    const url = `/v1/authentication/retrieveRegistrationToken?${query}`;

    return await identityClient.get<IdentityRetrieveRegistrationTokenResponse>(url, {
        headers: {
            'Content-Type': 'application/json',
            'x-petm-consumer-country': getCountry(),
        },
    });
}

export async function resetPassword(body: {
    email: string;
    redirectUri: string;
}): Promise<ApiClientResponse<IdentityResetPasswordResponse | IdentityErrorResponse | null>> {
    const consumerHeaderValue = isLocaleCA() ? 'DemandWareCA' : 'DemandWareUS';

    const url = IDENTITY_ENDPOINTS.resetPassword(consumerHeaderValue);

    const payload: IdentityResetPasswordPayload = {
        email: body.email,
        locale: getLocale(),
        flow: getENV('NEXT_PUBLIC_IDENTITY_API_WEB_FLOW'),
        flowVersion: getENV('NEXT_PUBLIC_IDENTITY_API_WEB_FLOW_VERSION'),
        janrainFormPayloadType: 'forgotPassword',
        redirectUri: body.redirectUri,
        // Identity API v2 requires reCaptchaToken field to be present, but doesn't validate it
        // when recaptcha is disabled. Sending dummyRecaptchaToken satisfies the requirement.
        // See: https://petsmart.atlassian.net/browse/AC-4605?focusedCommentId=630453
        reCaptchaToken: 'dummyRecaptchaToken',
    };

    return await identityClient.post<
        IdentityResetPasswordResponse | IdentityErrorResponse | null,
        IdentityResetPasswordPayload
    >(url, {
        headers: {
            'x-petm-consumer-country': getCountry(),
            'Content-Type': 'application/json',
            'X-Petm-Account-Skip-Recaptcha': 'false',
        },
        body: payload,
        additionalSuccessCodes: [400, 500],
    });
}

export async function changePassword(
    params: {
        newPassword: string;
        newPasswordConfirm: string;
        isForgot: boolean;
        currentPassword?: string;
    },
    accessToken: string
): Promise<ApiClientResponse<IdentityChangePasswordResponse | IdentityErrorResponse | null>> {
    const consumerHeaderValue = isLocaleCA() ? 'DemandWareCA' : 'DemandWareUS';

    const url = IDENTITY_ENDPOINTS.changePassword(consumerHeaderValue);
    const currentPassword = params.isForgot ? '' : params.currentPassword || '';

    const changePasswordPayload: IdentityChangePasswordPayload = {
        accessToken: accessToken,
        currentPassword: currentPassword,
        newPassword: params.newPassword,
        newPasswordConfirm: params.newPasswordConfirm,
        locale: getLocale(),
        flow: getENV('NEXT_PUBLIC_IDENTITY_API_WEB_FLOW'),
        flowVersion: getENV('NEXT_PUBLIC_IDENTITY_API_WEB_FLOW_VERSION'),
        form: params.isForgot ? 'changePasswordFormNoAuth' : 'changePasswordForm',
        janrainFormPayloadType: 'passwordChange',
        responseType: 'token',
    };

    type ChangePasswordApiResponse = IdentityChangePasswordResponse | IdentityErrorResponse;

    return await identityClient.post<ChangePasswordApiResponse | null, IdentityChangePasswordPayload>(url, {
        headers: {
            'Consumer-Country': getCountry(),
            'Content-Type': 'application/json',
        },
        body: changePasswordPayload,
        additionalSuccessCodes: [400],
    });
}

/**
 * Logout using Identity API
 * @param accessToken - The access token to logout
 * @returns Logout response or null if failed
 */
export async function logout(accessToken: string): Promise<ApiClientResponse<IdentityLogoutResponse | null>> {
    return await identityClient.post<IdentityLogoutResponse | null, never>(IDENTITY_ENDPOINTS.logout, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });
}
